import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

const LungAuscultationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const LungSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const LungTitle = styled.h4`
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  margin-bottom: 8px;
`;

const AreaGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const AreaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const AreaLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  gap: 10px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  text-align: left;
  cursor: pointer;

  &::after {
    content: '▼';
    margin-left: auto;
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 0 0 12px 12px;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1;
`;

const DropdownItem = styled.li`
  padding: 10px 16px;
  cursor: pointer;

  &:hover {
    background-color: #F0F0F0;
  }
`;

const SelectedOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const SelectedOption = styled.span`
  background-color: #E0E0E0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 14px;
`;

const LungAuscultation = ({ data, onChange }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = (side, area) => {
    const dropdownKey = `${side}-${area}`;
    setOpenDropdown(prevDropdown => prevDropdown === dropdownKey ? null : dropdownKey);
  };

  const handleOptionClick = (side, area, option) => {
    const currentOptions = data[side][area] || [];
    let newOptions;
    if (currentOptions.includes(option)) {
      newOptions = currentOptions.filter(item => item !== option);
    } else {
      newOptions = [...currentOptions, option];
    }

    onChange({
      ...data,
      [side]: {
        ...data[side],
        [area]: newOptions
      }
    });
  };

  const conditions = ['Clear', 'Crepitations', 'Wheeze', 'Effusion', 'Consolidation'];
  const areas = [
    { key: 'apex_anterior', label: 'Apex Anterior' },
    { key: 'base_posterior', label: 'Base Posterior' },
    { key: 'axillary', label: 'Axillary' }
  ];

  return (
    <LungAuscultationContainer>
      <ContentBox ref={dropdownRef}>
        {['left', 'right'].map((side) => (
          <LungSection key={side}>
            <LungTitle>{side.charAt(0).toUpperCase() + side.slice(1)} Lung</LungTitle>
            <AreaGroup>
              {areas.map(({ key, label }) => (
                <AreaColumn key={key}>
                  <AreaLabel>{label}</AreaLabel>
                  <DropdownContainer>
                    <DropdownButton onClick={() => handleDropdownToggle(side, key)}>
                      {data[side][key]?.length > 0 ? data[side][key].join(', ') : 'Select options'}
                    </DropdownButton>
                    {openDropdown === `${side}-${key}` && (
                      <DropdownList>
                        {conditions.map((condition) => (
                          <DropdownItem
                            key={condition}
                            onClick={() => handleOptionClick(side, key, condition)}
                          >
                            {condition}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </DropdownContainer>
                  <SelectedOptions>
                    {data[side][key]?.map((option) => (
                      <SelectedOption key={option}>{option}</SelectedOption>
                    ))}
                  </SelectedOptions>
                </AreaColumn>
              ))}
            </AreaGroup>
          </LungSection>
        ))}
      </ContentBox>
    </LungAuscultationContainer>
  );
};

export default LungAuscultation;
